import React, {useMemo} from 'react';
import {IconButton, styled} from '@mui/material';
import {designSystemToken, Icon} from '@lightricks/react-design-system';
import Dropdown, {type DropdownOption} from '@/components/dropdown/Dropdown';
import type {CtaDescriptor} from './HeaderActionsTypes';

const EMPTY_SELECTED_IDS: string[] = [];

const StyledIconButton = styled(IconButton)({
  height: '34px',
  width: '34px',
});

const StyledIcon = styled(Icon)({
  transform: 'scale(1.3)',
});

interface HeaderActionsOverflowProps {
  testID?: string;
  ctas: CtaDescriptor[];
}

function HeaderActionsOverflow({testID, ctas}: HeaderActionsOverflowProps) {
  const visibleCtas = useMemo(
    () => ctas.filter((cta) => cta.visible !== false),
    [ctas]
  );

  const options: DropdownOption[] = useMemo(
    () =>
      visibleCtas.map((cta) => ({
        id: cta.id,
        label: cta.label,
        startIcon: cta.icon ? (
          <Icon name={cta.icon} size="medium" appearance="neutral" />
        ) : undefined,
        disabled: cta.disabled,
      })),
    [visibleCtas]
  );

  if (visibleCtas.length === 0) {
    return null;
  }

  const handleChange = (option: DropdownOption) => {
    const cta = visibleCtas.find((c) => c.id === option.id);
    cta?.onClick();
  };

  return (
    <Dropdown
      label=""
      options={options}
      selectedIds={EMPTY_SELECTED_IDS}
      onChange={handleChange}
      selectionMode="single"
      menuWidth={260}
      showItemDividers={false}
      menuPositionHorizontal="right"
      renderTrigger={({onClick}) => (
        <StyledIconButton
          data-testid={testID}
          onClick={onClick}
          aria-label="More actions"
        >
          <StyledIcon
            name="Actions-More-Vertical"
            appearance="neutral"
            size="medium"
            color={designSystemToken('semantic.fg.primary')}
          />
        </StyledIconButton>
      )}
    />
  );
}

export default HeaderActionsOverflow;
